import * as THREE from "three";
import BaseThreeScene from "../../scenes/BaseThreeScene";
import { createMatrixBoardBase, createMatrixCellFrame } from "../../scenes/matrixPrimitives";
import { cellKey, getGridCellPosition, isInsideGrid, normalizeNumericGridInput } from "../../scenes/shared/gridUtils";

const DEFAULT_GRID = [
    [2, 1, 1],
    [1, 1, 0],
    [0, 1, 1],
];

const CELL_EMPTY = 0;
const CELL_FRESH = 1;
const CELL_ROTTEN = 2;

const CELL_SIZE = 1.1;
const CELL_GAP = 0.22;
const CELL_SPACING = CELL_SIZE + CELL_GAP;
const TILE_HEIGHT = 0.18;
const ORANGE_RADIUS = 0.28;

const EMPTY_TILE_COLOR = new THREE.Color(0x08101f);
const FRESH_TILE_COLOR = new THREE.Color(0x33210c);
const ROTTEN_TILE_COLOR = new THREE.Color(0x23143d);
const ACTIVE_TILE_COLOR = new THREE.Color(0x3b82f6);

const FRESH_ORANGE_COLOR = new THREE.Color(0xfb923c);
const ROTTEN_ORANGE_COLOR = new THREE.Color(0x7c3aed);
const ACTIVE_ORANGE_COLOR = new THREE.Color(0xfef3c7);

const NEIGHBOR_OFFSETS = [
    { dr: -1, dc: 0, label: "up" },
    { dr: 0, dc: 1, label: "right" },
    { dr: 1, dc: 0, label: "down" },
    { dr: 0, dc: -1, label: "left" },
];

function buildRottingSteps(grid) {
    const gridState = grid.map((row) => row.slice());
    const queue = [];
    const steps = [];
    let freshRemaining = 0;

    for (let row = 0; row < grid.length; row += 1) {
        for (let col = 0; col < grid[row].length; col += 1) {
            const value = gridState[row][col];

            if (value === CELL_FRESH) {
                freshRemaining += 1;
            } else if (value === CELL_ROTTEN) {
                queue.push([row, col]);
                steps.push({
                    type: "seed-rotten",
                    row,
                    col,
                });
            }
        }
    }

    if (freshRemaining === 0) {
        steps.push({
            type: "complete",
            didSucceed: true,
            minutes: 0,
            freshRemaining: 0,
        });
        return steps;
    }

    let minutesElapsed = 0;

    while (queue.length > 0 && freshRemaining > 0) {
        const frontierSize = queue.length;
        let newlyRotted = 0;

        steps.push({
            type: "start-minute",
            minute: minutesElapsed + 1,
            frontierSize,
            freshRemaining,
        });

        for (let index = 0; index < frontierSize; index += 1) {
            const [row, col] = queue.shift();

            steps.push({
                type: "dequeue-orange",
                row,
                col,
                minute: minutesElapsed + 1,
            });

            for (const direction of NEIGHBOR_OFFSETS) {
                const neighborRow = row + direction.dr;
                const neighborCol = col + direction.dc;

                let outcome = "out-of-bounds";

                if (isInsideGrid(gridState, neighborRow, neighborCol)) {
                    const neighborValue = gridState[neighborRow][neighborCol];

                    if (neighborValue === CELL_EMPTY) {
                        outcome = "empty";
                    } else if (neighborValue === CELL_ROTTEN) {
                        outcome = "rotten";
                    } else {
                        outcome = "fresh";
                    }
                }

                steps.push({
                    type: "inspect-neighbor",
                    row,
                    col,
                    direction: direction.label,
                    neighborRow,
                    neighborCol,
                    outcome,
                    minute: minutesElapsed + 1,
                });

                if (outcome === "fresh") {
                    gridState[neighborRow][neighborCol] = CELL_ROTTEN;
                    freshRemaining -= 1;
                    newlyRotted += 1;
                    queue.push([neighborRow, neighborCol]);

                    steps.push({
                        type: "rot-orange",
                        row: neighborRow,
                        col: neighborCol,
                        fromRow: row,
                        fromCol: col,
                        minute: minutesElapsed + 1,
                        freshRemaining,
                    });
                }
            }

            steps.push({
                type: "finish-orange",
                row,
                col,
                minute: minutesElapsed + 1,
            });
        }

        if (newlyRotted > 0) {
            minutesElapsed += 1;
            steps.push({
                type: "finish-minute",
                minute: minutesElapsed,
                newlyRotted,
                freshRemaining,
            });
        } else {
            break;
        }
    }

    steps.push({
        type: "complete",
        didSucceed: freshRemaining === 0,
        minutes: freshRemaining === 0 ? minutesElapsed : -1,
        freshRemaining,
    });

    return steps;
}

function createStepLogEntry(step, stepIndex) {
    const stepNumber = stepIndex + 1;
    const location = `[${step.row}, ${step.col}]`;

    switch (step.type) {
        case "seed-rotten":
            return {
                id: `rotting-oranges-step-${stepNumber}-seed-${step.row}-${step.col}`,
                stepIndex: stepNumber,
                kind: "Seed",
                title: `Seed rotten orange ${location}`,
                message: `Initial rotten orange ${location} is added to the multi-source BFS frontier before minute 1 begins.`,
            };

        case "start-minute":
            return {
                id: `rotting-oranges-step-${stepNumber}-start-minute-${step.minute}`,
                stepIndex: stepNumber,
                kind: "Minute",
                title: `Start minute ${step.minute}`,
                message: `Minute ${step.minute} begins with ${step.frontierSize} rotten oranges in the active frontier and ${step.freshRemaining} fresh oranges still remaining.`,
            };

        case "dequeue-orange":
            return {
                id: `rotting-oranges-step-${stepNumber}-dequeue-${step.row}-${step.col}`,
                stepIndex: stepNumber,
                kind: "Queue",
                title: `Process rotten orange ${location}`,
                message: `Rotten orange ${location} is removed from the current frontier and attempts to infect its four neighbors.`,
            };

        case "inspect-neighbor": {
            const neighborLocation = `[${step.neighborRow}, ${step.neighborCol}]`;
            let message = `Checks the ${step.direction} neighbor of ${location}. `;

            if (step.outcome === "out-of-bounds") {
                message += "That neighbor is outside the grid, so infection cannot spread there.";
            } else if (step.outcome === "empty") {
                message += `${neighborLocation} is empty, so there is no orange to rot.`;
            } else if (step.outcome === "rotten") {
                message += `${neighborLocation} is already rotten, so it does not create a new BFS expansion.`;
            } else {
                message += `${neighborLocation} is fresh, so it will rot and join the next frontier layer.`;
            }

            return {
                id: `rotting-oranges-step-${stepNumber}-inspect-${step.row}-${step.col}-${step.direction}`,
                stepIndex: stepNumber,
                kind: "Inspect",
                title: `Inspect ${step.direction} neighbor of ${location}`,
                message,
            };
        }

        case "rot-orange":
            return {
                id: `rotting-oranges-step-${stepNumber}-rot-${step.row}-${step.col}`,
                stepIndex: stepNumber,
                kind: "Rot",
                title: `Rot orange [${step.row}, ${step.col}]`,
                message: `Fresh orange [${step.row}, ${step.col}] decays from source [${step.fromRow}, ${step.fromCol}] during minute ${step.minute} and joins the next BFS frontier.`,
            };

        case "finish-orange":
            return {
                id: `rotting-oranges-step-${stepNumber}-finish-orange-${step.row}-${step.col}`,
                stepIndex: stepNumber,
                kind: "Finish",
                title: `Finish source ${location}`,
                message: `All four neighbors of ${location} have been evaluated for this minute's infection spread.`,
            };

        case "finish-minute":
            return {
                id: `rotting-oranges-step-${stepNumber}-finish-minute-${step.minute}`,
                stepIndex: stepNumber,
                kind: "Minute",
                title: `Finish minute ${step.minute}`,
                message: `Minute ${step.minute} ends after rotting ${step.newlyRotted} new oranges. Fresh oranges still remaining: ${step.freshRemaining}.`,
            };

        case "complete":
            return {
                id: `rotting-oranges-step-${stepNumber}-complete`,
                stepIndex: stepNumber,
                kind: "Result",
                title: step.didSucceed ? "Spread complete" : "Spread blocked",
                message: step.didSucceed
                    ? `All fresh oranges eventually rotted. Total minutes required: ${step.minutes}.`
                    : `Some fresh oranges remained isolated, so the answer is -1. Fresh oranges left: ${step.freshRemaining}.`,
            };

        default:
            return {
                id: `rotting-oranges-step-${stepNumber}-unknown`,
                stepIndex: stepNumber,
                kind: "Step",
                title: "Advance step",
                message: "Advances the rotting oranges visualization to the next state.",
            };
    }
}

export default class RottingOrangesScene extends BaseThreeScene {
    setup(props) {
        this.grid = normalizeNumericGridInput(props.input, DEFAULT_GRID);
        const maxDimension = Math.max(this.grid.length, this.grid[0].length);
        const cameraHeight = 4.6 + maxDimension * 0.72;
        const cameraDepth = 5.2 + maxDimension * 0.54;

        this.scene.background = new THREE.Color(0x040816);
        this.scene.fog = new THREE.Fog(0x040816, 10, 22);
        this.camera.position.set(0, cameraHeight, cameraDepth);
        this.camera.lookAt(0, 0.5, 0);

        const ambientLight = new THREE.AmbientLight(0xffffff, 1.05);
        this.scene.add(ambientLight);

        const keyLight = new THREE.DirectionalLight(0xffffff, 1.85);
        keyLight.position.set(5, 8, 4);
        this.scene.add(keyLight);

        const orangeLight = new THREE.PointLight(0xfb923c, 20, 20, 2);
        orangeLight.position.set(-4, 4, 5);
        this.scene.add(orangeLight);

        const rotLight = new THREE.PointLight(0x7c3aed, 18, 18, 2);
        rotLight.position.set(4, 4, -2);
        this.scene.add(rotLight);

        this.boardRoot = new THREE.Group();
        this.scene.add(this.boardRoot);

        this.tileMeshes = new Map();
        this.orangeGroups = new Map();
        this.cellPositions = new Map();

        this.presentationTime = 0;

        this.buildBoard();
        this.buildIndicators();
        this.resetAnimationState();
        this.advanceStep();
    }

    buildBoard() {
        const rows = this.grid.length;
        const cols = this.grid[0].length;
        const boardWidth = cols * CELL_SPACING + 1.05;
        const boardDepth = rows * CELL_SPACING + 1.05;

        const boardBase = createMatrixBoardBase({
            width: boardWidth,
            depth: boardDepth,
            baseHeight: 0.24,
            emissiveIntensity: 0.34,
            roughness: 0.76,
            metalness: 0.08,
            surfaceInset: 0.18,
            surfaceOpacity: 0.82,
            baseY: -0.13,
            surfaceY: -0.005,
        });
        this.boardRoot.add(boardBase);

        for (let row = 0; row < rows; row += 1) {
            for (let col = 0; col < cols; col += 1) {
                const key = cellKey(row, col);
                const value = this.grid[row][col];
                const position = getGridCellPosition(
                    row,
                    col,
                    rows,
                    cols,
                    CELL_SPACING
                );

                this.cellPositions.set(key, position.clone());

                const tile = new THREE.Mesh(
                    new THREE.BoxGeometry(CELL_SIZE, TILE_HEIGHT, CELL_SIZE),
                    new THREE.MeshStandardMaterial({
                        color:
                            value === CELL_EMPTY
                                ? EMPTY_TILE_COLOR.getHex()
                                : value === CELL_FRESH
                                    ? FRESH_TILE_COLOR.getHex()
                                    : ROTTEN_TILE_COLOR.getHex(),
                        emissive:
                            value === CELL_EMPTY
                                ? EMPTY_TILE_COLOR.getHex()
                                : value === CELL_FRESH
                                    ? FRESH_TILE_COLOR.getHex()
                                    : ROTTEN_TILE_COLOR.getHex(),
                        emissiveIntensity: value === CELL_EMPTY ? 0.18 : 0.26,
                        roughness: 0.6,
                        metalness: 0.08,
                    })
                );
                tile.position.copy(position);
                tile.position.y = TILE_HEIGHT * 0.5;
                tile.userData = {
                    row,
                    col,
                    baseX: position.x,
                    baseZ: position.z,
                };

                this.tileMeshes.set(key, tile);
                this.boardRoot.add(tile);

                if (value !== CELL_EMPTY) {
                    const group = new THREE.Group();

                    const sphere = new THREE.Mesh(
                        new THREE.SphereGeometry(ORANGE_RADIUS, 28, 28),
                        new THREE.MeshStandardMaterial({
                            color:
                                value === CELL_FRESH
                                    ? FRESH_ORANGE_COLOR.getHex()
                                    : ROTTEN_ORANGE_COLOR.getHex(),
                            emissive:
                                value === CELL_FRESH
                                    ? FRESH_ORANGE_COLOR.getHex()
                                    : ROTTEN_ORANGE_COLOR.getHex(),
                            emissiveIntensity: value === CELL_FRESH ? 0.24 : 0.36,
                            roughness: 0.45,
                            metalness: 0.05,
                        })
                    );

                    const stem = new THREE.Mesh(
                        new THREE.CylinderGeometry(0.04, 0.06, 0.16, 10),
                        new THREE.MeshStandardMaterial({
                            color: 0x3f6212,
                            emissive: 0x1a2e05,
                            emissiveIntensity: 0.16,
                            roughness: 0.82,
                            metalness: 0.02,
                        })
                    );

                    sphere.position.y = 0;
                    stem.position.y = ORANGE_RADIUS + 0.06;
                    stem.rotation.z = 0.22;

                    group.add(sphere);
                    group.add(stem);
                    group.position.copy(position);
                    group.position.y = TILE_HEIGHT + ORANGE_RADIUS * 0.95;
                    group.userData = {
                        row,
                        col,
                        sphere,
                        stem,
                        baseY: TILE_HEIGHT + ORANGE_RADIUS * 0.95,
                    };

                    this.orangeGroups.set(key, group);
                    this.boardRoot.add(group);
                }
            }
        }
    }

    buildIndicators() {
        this.activeFrame = createMatrixCellFrame(CELL_SIZE + 0.18, 0xffffff);
        this.boardRoot.add(this.activeFrame);

        this.neighborFrame = createMatrixCellFrame(CELL_SIZE + 0.08, 0xfde68a);
        this.boardRoot.add(this.neighborFrame);

        this.infectionBeamGeometry = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(),
            new THREE.Vector3(),
        ]);
        this.infectionBeam = new THREE.Line(
            this.infectionBeamGeometry,
            new THREE.LineBasicMaterial({
                color: 0xfbbf24,
                transparent: true,
                opacity: 0,
            })
        );
        this.boardRoot.add(this.infectionBeam);

        this.rotPulse = new THREE.Mesh(
            new THREE.RingGeometry(0.22, 0.34, 48),
            new THREE.MeshBasicMaterial({
                color: 0xfbbf24,
                transparent: true,
                opacity: 0,
                side: THREE.DoubleSide,
            })
        );
        this.rotPulse.rotation.x = -Math.PI * 0.5;
        this.boardRoot.add(this.rotPulse);
    }

    resetAnimationState() {
        this.steps = buildRottingSteps(this.grid);
        this.stepIndex = -1;
        this.currentStep = null;
        this.currentStepElapsed = 0;

        this.activeCellKey = null;
        this.activeNeighborCellKey = null;
        this.activeNeighborOutcome = null;

        this.emptyCells = new Set();
        this.freshCells = new Set();
        this.rottenCells = new Set();
        this.frontier = [];

        for (let row = 0; row < this.grid.length; row += 1) {
            for (let col = 0; col < this.grid[row].length; col += 1) {
                const key = cellKey(row, col);
                const value = this.grid[row][col];

                if (value === CELL_EMPTY) {
                    this.emptyCells.add(key);
                } else if (value === CELL_FRESH) {
                    this.freshCells.add(key);
                } else {
                    this.rottenCells.add(key);
                    this.frontier.push(key);
                }
            }
        }

        this.minute = 0;
        this.resultMinutes = null;
        this.didSucceed = null;

        this.infectionBeamState = {
            active: false,
            from: new THREE.Vector3(),
            to: new THREE.Vector3(),
            elapsed: 0,
            duration: 0.48,
        };

        this.rotPulseState = {
            active: false,
            center: new THREE.Vector3(),
            elapsed: 0,
            duration: 0.8,
        };

        this.presentationTime = 0;
        this.resetStepLog([]);

        this.replaceUiState({
            minute: 0,
            freshRemaining: this.freshCells.size,
            frontierSize: this.frontier.length,
            activeCellLabel: "-",
            resultLabel: "In Progress",
        });

        this.syncPlaybackState();
    }

    syncUiState() {
        let resultLabel = "In Progress";

        if (this.didSucceed === true) {
            resultLabel = `Minutes: ${this.resultMinutes}`;
        } else if (this.didSucceed === false) {
            resultLabel = "Unreachable";
        }

        this.replaceUiState({
            minute: this.minute,
            freshRemaining: this.freshCells.size,
            frontierSize: this.frontier.length,
            activeCellLabel: this.activeCellKey
                ? `(${this.activeCellKey.replace(":", ", ")})`
                : "-",
            resultLabel,
        });
    }

    getStepDuration(step) {
        switch (step.type) {
            case "seed-rotten":
                return 0.32;
            case "start-minute":
                return 0.55;
            case "dequeue-orange":
                return 0.46;
            case "inspect-neighbor":
                return 0.36;
            case "rot-orange":
                return 0.42;
            case "finish-orange":
                return 0.3;
            case "finish-minute":
                return 0.62;
            case "complete":
                return 1.8;
            default:
                return 0.4;
        }
    }

    syncPlaybackState() {
        this.setPlaybackState({
            stepIndex: Math.max(0, this.stepIndex + 1),
            stepCount: this.steps.length,
            hasNextStep: this.stepIndex < this.steps.length - 1,
            isComplete:
                this.currentStep !== null && this.currentStep.type === "complete",
        });
    }

    advanceStep() {
        const nextStepIndex = this.stepIndex + 1;

        if (nextStepIndex >= this.steps.length) {
            this.syncPlaybackState();
            this.syncUiState();
            return false;
        }

        this.stepIndex = nextStepIndex;
        this.currentStepElapsed = 0;
        this.currentStep = this.steps[this.stepIndex] || null;
        this.applyStep(this.currentStep);
        this.appendStepLog(createStepLogEntry(this.currentStep, this.stepIndex));
        this.syncPlaybackState();
        this.syncUiState();

        return true;
    }

    applyStep(step) {
        if (!step) {
            return;
        }

        this.activeNeighborCellKey = null;
        this.activeNeighborOutcome = null;

        switch (step.type) {
            case "seed-rotten": {
                this.activeCellKey = cellKey(step.row, step.col);
                break;
            }

            case "start-minute": {
                this.minute = step.minute;
                this.activeCellKey = null;
                break;
            }

            case "dequeue-orange": {
                const key = cellKey(step.row, step.col);
                this.activeCellKey = key;

                const frontierIndex = this.frontier.indexOf(key);
                if (frontierIndex >= 0) {
                    this.frontier.splice(frontierIndex, 1);
                }
                break;
            }

            case "inspect-neighbor": {
                this.activeCellKey = cellKey(step.row, step.col);

                if (isInsideGrid(step.neighborRow, step.neighborCol)) {
                    this.activeNeighborCellKey = cellKey(
                        step.neighborRow,
                        step.neighborCol
                    );
                    this.activeNeighborOutcome = step.outcome;
                }
                break;
            }

            case "rot-orange": {
                const key = cellKey(step.row, step.col);
                this.freshCells.delete(key);
                this.rottenCells.add(key);

                if (!this.frontier.includes(key)) {
                    this.frontier.push(key);
                }

                this.startInfectionBeam(
                    step.fromRow,
                    step.fromCol,
                    step.row,
                    step.col
                );
                this.startRotPulse(step.row, step.col);
                break;
            }

            case "finish-orange": {
                this.activeNeighborCellKey = null;
                this.activeNeighborOutcome = null;
                break;
            }

            case "finish-minute": {
                this.minute = step.minute;
                break;
            }

            case "complete": {
                this.activeCellKey = null;
                this.activeNeighborCellKey = null;
                this.activeNeighborOutcome = null;
                this.frontier = [];
                this.didSucceed = step.didSucceed;
                this.resultMinutes = step.minutes;
                break;
            }

            default:
                break;
        }
    }

    onResetPlayback() {
        this.resetAnimationState();
        this.advanceStep();
    }

    onStepForward() {
        this.advanceStep();
    }

    startInfectionBeam(fromRow, fromCol, toRow, toCol) {
        const from = this.cellPositions.get(cellKey(fromRow, fromCol)).clone();
        const to = this.cellPositions.get(cellKey(toRow, toCol)).clone();

        from.y = TILE_HEIGHT + ORANGE_RADIUS * 0.95;
        to.y = TILE_HEIGHT + ORANGE_RADIUS * 0.95;

        this.infectionBeamState.active = true;
        this.infectionBeamState.elapsed = 0;
        this.infectionBeamState.from.copy(from);
        this.infectionBeamState.to.copy(to);
    }

    startRotPulse(row, col) {
        const center = this.cellPositions.get(cellKey(row, col)).clone();
        center.y = TILE_HEIGHT + 0.04;

        this.rotPulseState.active = true;
        this.rotPulseState.elapsed = 0;
        this.rotPulseState.center.copy(center);
    }

    update(delta) {
        if (!this.boardRoot || !this.currentStep) {
            return;
        }

        const playbackDelta = this.isPlaying ? delta : 0;
        this.presentationTime += playbackDelta;
        this.currentStepElapsed += playbackDelta;

        if (
            this.isPlaying &&
            this.currentStepElapsed >= this.getStepDuration(this.currentStep)
        ) {
            const advanced = this.advanceStep();
            if (!advanced) {
                this.pause();
            }
        }

        this.updateBoardMotion(this.presentationTime);
        this.updateTiles();
        this.updateOranges(this.presentationTime);
        this.updateIndicators(this.presentationTime);
        this.updateInfectionBeam(playbackDelta);
        this.updateRotPulse(playbackDelta);
    }

    updateBoardMotion(elapsed) {
        this.boardRoot.rotation.y = Math.sin(elapsed * 0.16) * 0.035;
        this.boardRoot.position.y = Math.sin(elapsed * 0.45) * 0.03;
    }

    updateTiles() {
        this.tileMeshes.forEach((tile, key) => {
            const isEmpty = this.emptyCells.has(key);
            const isFresh = this.freshCells.has(key);
            const isRotten = this.rottenCells.has(key);
            const isActive = key === this.activeCellKey;

            let targetColor = EMPTY_TILE_COLOR;
            let targetEmissiveIntensity = 0.18;

            if (isFresh) {
                targetColor = FRESH_TILE_COLOR;
                targetEmissiveIntensity = 0.24;
            }

            if (isRotten) {
                targetColor = ROTTEN_TILE_COLOR;
                targetEmissiveIntensity = 0.3;
            }

            if (isActive) {
                targetColor = ACTIVE_TILE_COLOR;
                targetEmissiveIntensity = 0.5;
            }

            tile.material.color.lerp(targetColor, 0.16);
            tile.material.emissive.lerp(targetColor, 0.16);
            tile.material.emissiveIntensity = THREE.MathUtils.lerp(
                tile.material.emissiveIntensity,
                targetEmissiveIntensity,
                0.16
            );

            if (isEmpty) {
                tile.position.y = THREE.MathUtils.lerp(
                    tile.position.y,
                    TILE_HEIGHT * 0.42,
                    0.16
                );
            } else {
                tile.position.y = THREE.MathUtils.lerp(
                    tile.position.y,
                    TILE_HEIGHT * 0.5,
                    0.16
                );
            }
        });
    }

    updateOranges(elapsed) {
        this.orangeGroups.forEach((group, key) => {
            const isFresh = this.freshCells.has(key);
            const isRotten = this.rottenCells.has(key);
            const isActive = key === this.activeCellKey;
            const isNeighbor = key === this.activeNeighborCellKey;

            const sphere = group.userData.sphere;
            const stem = group.userData.stem;

            let targetColor = FRESH_ORANGE_COLOR;
            let targetEmissiveIntensity = 0.24;
            let targetScale = 1;

            if (isRotten) {
                targetColor = ROTTEN_ORANGE_COLOR;
                targetEmissiveIntensity = 0.34;
                targetScale = 0.98;
            }

            if (isNeighbor && this.activeNeighborOutcome === "fresh") {
                targetColor = ACTIVE_ORANGE_COLOR;
                targetEmissiveIntensity = 0.7;
                targetScale = 1.1;
            }

            if (isActive) {
                targetColor = ACTIVE_ORANGE_COLOR;
                targetEmissiveIntensity = 0.85;
                targetScale = 1.16 + Math.sin(elapsed * 5.2) * 0.03;
            }

            const bob = Math.sin(elapsed * 1.8 + group.userData.row * 0.7 + group.userData.col * 0.45) * 0.03;
            group.position.y = THREE.MathUtils.lerp(
                group.position.y,
                group.userData.baseY + bob,
                0.14
            );

            const scaleValue = THREE.MathUtils.lerp(
                group.scale.x,
                targetScale,
                0.14
            );
            group.scale.setScalar(scaleValue);

            sphere.material.color.lerp(targetColor, 0.16);
            sphere.material.emissive.lerp(targetColor, 0.16);
            sphere.material.emissiveIntensity = THREE.MathUtils.lerp(
                sphere.material.emissiveIntensity,
                targetEmissiveIntensity,
                0.16
            );

            stem.material.emissiveIntensity = THREE.MathUtils.lerp(
                stem.material.emissiveIntensity,
                isRotten ? 0.08 : 0.16,
                0.16
            );

            if (!isFresh && !isRotten) {
                group.visible = false;
            } else {
                group.visible = true;
            }
        });
    }

    updateIndicators(elapsed) {
        if (this.activeCellKey && this.cellPositions.has(this.activeCellKey)) {
            const activePosition = this.cellPositions.get(this.activeCellKey).clone();
            activePosition.y = TILE_HEIGHT + 0.03;

            this.activeFrame.position.lerp(activePosition, 0.18);
            this.activeFrame.material.opacity = THREE.MathUtils.lerp(
                this.activeFrame.material.opacity,
                0.95,
                0.18
            );
            this.activeFrame.scale.setScalar(1 + Math.sin(elapsed * 4.5) * 0.05);
        } else {
            this.activeFrame.material.opacity = THREE.MathUtils.lerp(
                this.activeFrame.material.opacity,
                0,
                0.18
            );
        }

        if (
            this.activeNeighborCellKey &&
            this.cellPositions.has(this.activeNeighborCellKey)
        ) {
            const neighborPosition = this.cellPositions.get(this.activeNeighborCellKey).clone();
            neighborPosition.y = TILE_HEIGHT + 0.025;

            let frameColor = 0xfde68a;

            if (this.activeNeighborOutcome === "fresh") {
                frameColor = 0x86efac;
            } else if (this.activeNeighborOutcome === "empty") {
                frameColor = 0xf87171;
            } else if (this.activeNeighborOutcome === "rotten") {
                frameColor = 0xc4b5fd;
            }

            this.neighborFrame.material.color.setHex(frameColor);
            this.neighborFrame.position.lerp(neighborPosition, 0.2);
            this.neighborFrame.material.opacity = THREE.MathUtils.lerp(
                this.neighborFrame.material.opacity,
                0.9,
                0.2
            );
        } else {
            this.neighborFrame.material.opacity = THREE.MathUtils.lerp(
                this.neighborFrame.material.opacity,
                0,
                0.18
            );
        }
    }

    updateInfectionBeam(delta) {
        const state = this.infectionBeamState;

        if (!state.active) {
            this.infectionBeam.material.opacity = THREE.MathUtils.lerp(
                this.infectionBeam.material.opacity,
                0,
                0.18
            );
            return;
        }

        state.elapsed += delta;
        const t = Math.min(state.elapsed / state.duration, 1);
        const eased = THREE.MathUtils.smoothstep(t, 0, 1);

        const currentEnd = new THREE.Vector3().lerpVectors(
            state.from,
            state.to,
            eased
        );

        this.infectionBeamGeometry.setFromPoints([state.from, currentEnd]);
        this.infectionBeam.material.opacity = 0.95 * Math.sin(eased * Math.PI);

        if (t >= 1) {
            state.active = false;
        }
    }

    updateRotPulse(delta) {
        const state = this.rotPulseState;

        if (!state.active) {
            this.rotPulse.material.opacity = THREE.MathUtils.lerp(
                this.rotPulse.material.opacity,
                0,
                0.18
            );
            return;
        }

        state.elapsed += delta;
        const t = Math.min(state.elapsed / state.duration, 1);
        const eased = THREE.MathUtils.smoothstep(t, 0, 1);

        this.rotPulse.position.copy(state.center);
        this.rotPulse.scale.setScalar(0.85 + eased * 1.1);
        this.rotPulse.material.opacity = 0.82 * Math.sin(eased * Math.PI);

        if (t >= 1) {
            state.active = false;
        }
    }
}